import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";

export default function Login() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mode, setMode] = useState("entrar");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "entrar") {
        await login(form.email, form.password);
      } else {
        await register(form.name, form.email, form.password);
      }
      navigate(location.state?.from || "/comunidade", { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#050505] px-6" data-testid="login-page">
      <div className="grain-overlay" aria-hidden="true" />
      <div className="pointer-events-none fixed inset-0 bg-[radial-gradient(ellipse_at_50%_20%,rgba(212,175,55,0.08),transparent_55%)]" />
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        className="relative w-full max-w-md border border-gold/20 bg-[#0a0a0a]/90 p-10 backdrop-blur-xl"
      >
        <Link to="/" className="flex flex-col items-center" data-testid="login-brand-link">
          <span className="flex h-16 w-16 items-center justify-center rounded-full border border-gold/50 font-serif text-gold">REF</span>
          <span className="mt-4 font-serif text-lg tracking-[0.3em] text-zinc-100">REFLLEXIVE</span>
        </Link>

        <div className="mt-8 grid grid-cols-2 border border-gold/20" data-testid="auth-mode-toggle">
          <button
            onClick={() => { setMode("entrar"); setError(""); }}
            className={`py-3 text-xs font-semibold uppercase tracking-[0.2em] transition-colors ${mode === "entrar" ? "bg-gold text-black" : "text-zinc-400 hover:text-gold"}`}
            data-testid="mode-entrar-button"
          >
            Entrar
          </button>
          <button
            onClick={() => { setMode("cadastrar"); setError(""); }}
            className={`py-3 text-xs font-semibold uppercase tracking-[0.2em] transition-colors ${mode === "cadastrar" ? "bg-gold text-black" : "text-zinc-400 hover:text-gold"}`}
            data-testid="mode-cadastrar-button"
          >
            Criar conta
          </button>
        </div>

        <form onSubmit={submit} className="mt-8 flex flex-col gap-5" data-testid="auth-form">
          {mode === "cadastrar" && (
            <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input-dark" placeholder="Seu nome" data-testid="register-name-input" />
          )}
          <input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="input-dark" placeholder="Seu e-mail" data-testid="auth-email-input" />
          <input type="password" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="input-dark" placeholder="Sua senha (mín. 6 caracteres)" data-testid="auth-password-input" />
          {mode === "entrar" && (
            <Link to="/recuperar-senha" className="-mt-2 text-right text-xs text-zinc-500 transition-colors hover:text-gold" data-testid="forgot-password-link">
              Esqueci minha senha
            </Link>
          )}
          {location.state?.reset && (
            <p className="border border-gold/30 bg-gold/10 px-4 py-3 text-sm text-gold" data-testid="reset-success-message">Senha redefinida com sucesso. Entre com a nova senha.</p>
          )}
          {error && <p className="border border-red-900/50 bg-red-950/30 px-4 py-3 text-sm text-red-300" data-testid="auth-error-message">{error}</p>}
          <button type="submit" disabled={loading} className="btn-gold w-full" data-testid="auth-submit-button">
            {loading ? "Aguarde…" : mode === "entrar" ? "Entrar na comunidade" : "Criar minha conta"}
          </button>
        </form>

        <p className="mt-8 text-center text-xs text-zinc-500">
          Ao entrar, você acessa a mentoria e o conteúdo exclusivo da comunidade.
        </p>
      </motion.div>
    </div>
  );
}
